import type { ExerciseTemplate } from '@yuan-math/exercises';
import { gcd, lcm, withThousandsSeparators } from '@yuan-math/math';
import { choiceExercise, numericExercise, options, template, trap } from './authoring.ts';

/** Number sense and whole-number arithmetic. */
export const ARITHMETIC_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'count-objects',
    conceptId: 'counting',
    description: 'Count objects arranged in an array.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const rows = rng.int(1, difficulty === 1 ? 3 : 5);
      const columns = rng.int(2, difficulty === 1 ? 5 : 8);
      const total = rows * columns;
      return numericExercise({
        prompt: 'How many counters are there altogether?',
        figure: { kind: 'array', rows, columns },
        value: total,
        summary: `${rows} rows of ${columns} makes ${total}.`,
        hint: 'Count one row, then count how many rows there are.',
        steps: [
          { label: 'Count one row.', latex: String(columns) },
          { label: 'Count the rows.', latex: String(rows) },
          { label: 'Multiply, or count on in groups.', latex: `${rows} \\times ${columns} = ${total}` },
        ],
      });
    },
  }),

  template({
    id: 'count-on',
    conceptId: 'counting',
    description: 'Count on from a starting number.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const start = rng.int(3, difficulty === 1 ? 12 : 40);
      const step = rng.int(2, difficulty === 1 ? 5 : 9);
      return numericExercise({
        prompt: `Start at ${start} and count on ${step}. Where do you land?`,
        latex: `${start} + ${step}`,
        value: start + step,
        summary: `Counting on ${step} from ${start} reaches ${start + step}.`,
        hint: 'Say the numbers after the starting number, one for each step.',
        traps: [trap(start + step - 1, 'regrouping-error')],
      });
    },
  }),

  template({
    id: 'place-value-digit',
    conceptId: 'place-value',
    description: 'State the value of a digit in a multi-digit number.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const length = difficulty === 1 ? 3 : difficulty === 2 ? 4 : 5;
      const digits = Array.from({ length }, (_, i) => (i === 0 ? rng.int(1, 9) : rng.int(0, 9)));
      const index = rng.int(0, length - 1);
      const digit = digits[index] as number;
      const placeValue = 10 ** (length - 1 - index);
      const number = Number(digits.join(''));
      const placeNames = ['ones', 'tens', 'hundreds', 'thousands', 'ten thousands'];
      return numericExercise({
        prompt: `In the number ${withThousandsSeparators(number)}, what is the value of the digit ${digit}${countOccurrences(digits, digit) > 1 ? ` in the ${placeNames[length - 1 - index]} place` : ''}?`,
        figure: { kind: 'place-value', digits: digits.join(''), places: placeNames.slice(0, length).reverse() },
        value: digit * placeValue,
        summary: `The ${digit} sits in the ${placeNames[length - 1 - index]} place, so it is worth ${digit} × ${placeValue} = ${digit * placeValue}.`,
        hint: 'Name the places from the right: ones, tens, hundreds…',
        steps: [
          { label: 'Find the place.', latex: `\\text{${placeNames[length - 1 - index]}}` },
          { label: 'Multiply the digit by the place value.', latex: `${digit} \\times ${placeValue} = ${digit * placeValue}` },
        ],
        // Only a trap when the place value is not 1 — in the ones place the
        // bare digit is the correct answer, not a misconception.
        ...(placeValue > 1 ? { traps: [trap(digit, 'place-value-shift')] } : {}),
      });
    },
  }),

  template({
    id: 'rounding-nearest',
    conceptId: 'rounding',
    description: 'Round a number to a given place.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const place = difficulty === 2 ? 10 : difficulty === 3 ? 100 : 1000;
      const placeName = place === 10 ? 'ten' : place === 100 ? 'hundred' : 'thousand';
      const number = rng.int(place, place * 10 - 1);
      const answer = Math.round(number / place) * place;
      const down = Math.floor(number / place) * place;
      return numericExercise({
        prompt: `Round ${withThousandsSeparators(number)} to the nearest ${placeName}.`,
        value: answer,
        summary: `${number} lies between ${down} and ${down + place}, and is nearer ${answer}.`,
        hint: `Look at the digit just to the right of the ${placeName}s place.`,
        steps: [
          { label: 'Find the two neighbours.', latex: `${down} \\text{ and } ${down + place}` },
          { label: 'Check the deciding digit.', latex: String(Math.floor((number % place) / (place / 10))) },
          { label: '5 or more rounds up.', latex: String(answer) },
        ],
        traps: [trap(answer === down ? down + place : down, 'regrouping-error')],
      });
    },
  }),

  template({
    id: 'add-multi-digit',
    conceptId: 'multi-digit-addition',
    description: 'Add two multi-digit numbers with carrying.',
    difficulties: [1, 2, 3, 4],
    build: ({ rng, difficulty }) => {
      const max = [0, 99, 999, 9999, 99999][difficulty] as number;
      const min = [0, 10, 100, 1000, 10000][difficulty] as number;
      const a = rng.int(min, max);
      const b = rng.int(min, max);
      return numericExercise({
        prompt: 'Add.',
        latex: `${a} + ${b}`,
        value: a + b,
        summary: `Adding column by column from the right gives ${a + b}.`,
        hint: 'Line up the places and add from the right, carrying when a column passes 9.',
        steps: [
          { label: 'Ones column.', latex: `${a % 10} + ${b % 10} = ${(a % 10) + (b % 10)}` },
          { label: 'Continue leftwards, carrying as needed.', latex: `${a} + ${b}` },
          { label: 'Total.', latex: String(a + b) },
        ],
        traps: [trap(a + b - 10, 'regrouping-error')],
      });
    },
  }),

  template({
    id: 'subtract-multi-digit',
    conceptId: 'multi-digit-subtraction',
    description: 'Subtract two multi-digit numbers, with regrouping.',
    difficulties: [1, 2, 3, 4],
    build: ({ rng, difficulty }) => {
      const max = [0, 99, 999, 9999, 99999][difficulty] as number;
      const min = [0, 10, 100, 1000, 10000][difficulty] as number;
      const a = rng.int(min + Math.floor(max / 2), max);
      const b = rng.int(min, a - 1);
      // The classic error: subtracting the smaller digit from the larger in
      // each column, ignoring which is on top.
      const columnwise = Number(
        String(a)
          .padStart(String(a).length, '0')
          .split('')
          .map((digit, index) => {
            const bDigits = String(b).padStart(String(a).length, '0');
            return Math.abs(Number(digit) - Number(bDigits[index]));
          })
          .join(''),
      );
      return numericExercise({
        prompt: 'Subtract.',
        latex: `${a} - ${b}`,
        value: a - b,
        summary: `Working right to left and regrouping where needed gives ${a - b}.`,
        hint: 'When the top digit is smaller than the bottom one, trade from the next place.',
        steps: [
          { label: 'Subtract column by column from the right.', latex: `${a} - ${b}` },
          { label: 'Regroup wherever the top digit is too small.', latex: '\\text{trade one from the next place}' },
          { label: 'Check by adding back.', latex: `${a - b} + ${b} = ${a}` },
        ],
        // Only a trap when regrouping was actually required; without it the
        // column-wise difference is simply the right answer.
        ...(columnwise !== a - b ? { traps: [trap(columnwise, 'regrouping-error')] } : {}),
      });
    },
  }),

  template({
    id: 'multiplication-fact',
    conceptId: 'multiplication-facts',
    description: 'Recall or reconstruct a multiplication fact.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 1 ? 5 : difficulty === 2 ? 10 : 12;
      const a = rng.int(2, ceiling);
      const b = rng.int(2, ceiling);
      return numericExercise({
        prompt: 'Multiply.',
        latex: `${a} \\times ${b}`,
        value: a * b,
        summary: `${a} groups of ${b} is ${a * b}.`,
        hint: `Build it from a fact you know: ${a} × ${b} is ${a} lots of ${b}.`,
        steps: [
          { label: 'Read it as equal groups.', latex: `${a} \\text{ groups of } ${b}` },
          { label: 'Multiply.', latex: `${a} \\times ${b} = ${a * b}` },
        ],
        traps: [trap(a + b, 'multiply-then-add-wrong-order')],
      });
    },
  }),

  template({
    id: 'multiplication-word',
    conceptId: 'multiplication-facts',
    description: 'A word problem requiring multiplication.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const rows = rng.int(3, difficulty === 2 ? 8 : 12);
      const per = rng.int(3, difficulty === 2 ? 9 : 12);
      const scenario = rng.pick([
        { text: `A theatre has ${rows} rows with ${per} seats in each row.`, question: 'How many seats are there?' },
        { text: `There are ${rows} boxes with ${per} pencils in each.`, question: 'How many pencils altogether?' },
        { text: `A garden has ${rows} rows of ${per} plants.`, question: 'How many plants are there?' },
      ]);
      return numericExercise({
        prompt: `${scenario.text} ${scenario.question}`,
        value: rows * per,
        summary: `${rows} equal groups of ${per} gives ${rows * per}.`,
        hint: 'Equal groups means multiply.',
        steps: [{ label: 'Multiply the number of groups by the size of each.', latex: `${rows} \\times ${per} = ${rows * per}` }],
        traps: [trap(rows + per, 'multiply-then-add-wrong-order')],
      });
    },
  }),

  template({
    id: 'division-exact',
    conceptId: 'division-meaning',
    description: 'Divide with no remainder.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 1 ? 5 : difficulty === 2 ? 9 : 12;
      const divisor = rng.int(2, ceiling);
      const quotient = rng.int(2, ceiling);
      const dividend = divisor * quotient;
      return numericExercise({
        prompt: 'Divide.',
        latex: `${dividend} \\div ${divisor}`,
        value: quotient,
        summary: `${divisor} × ${quotient} = ${dividend}, so ${dividend} ÷ ${divisor} = ${quotient}.`,
        hint: `Ask yourself: ${divisor} times what gives ${dividend}?`,
        steps: [
          { label: 'Turn it into a multiplication question.', latex: `${divisor} \\times ? = ${dividend}` },
          { label: 'Recall the fact.', latex: `${divisor} \\times ${quotient} = ${dividend}` },
        ],
        traps: [trap(dividend * divisor, 'divide-wrong-direction')],
      });
    },
  }),

  template({
    id: 'division-remainder',
    conceptId: 'division-remainders',
    description: 'Divide with a remainder and interpret it.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const divisor = rng.int(3, difficulty === 2 ? 6 : 9);
      const quotient = rng.int(2, difficulty === 4 ? 15 : 9);
      const remainder = rng.int(1, divisor - 1);
      const dividend = divisor * quotient + remainder;
      const asksRemainder = rng.bool();
      return numericExercise({
        prompt: asksRemainder
          ? `What is the remainder when ${dividend} is divided by ${divisor}?`
          : `${dividend} sweets are shared equally between ${divisor} children. How many does each child get?`,
        latex: `${dividend} \\div ${divisor}`,
        value: asksRemainder ? remainder : quotient,
        summary: `${dividend} ÷ ${divisor} = ${quotient} remainder ${remainder}.`,
        hint: `How many whole groups of ${divisor} fit into ${dividend}, and what is left?`,
        steps: [
          { label: 'Find the largest multiple that fits.', latex: `${divisor} \\times ${quotient} = ${divisor * quotient}` },
          { label: 'Subtract to find what is left.', latex: `${dividend} - ${divisor * quotient} = ${remainder}` },
          { label: 'Check the remainder is smaller than the divisor.', latex: `${remainder} < ${divisor}` },
        ],
        traps: asksRemainder ? [trap(quotient, 'ignored-remainder')] : [trap(quotient + 1, 'ignored-remainder')],
      });
    },
  }),

  template({
    id: 'factors-list',
    conceptId: 'factors-multiples',
    description: 'Identify whether one number is a factor of another.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const number = rng.int(12, difficulty === 2 ? 40 : 100);
      const properFactors: number[] = [];
      for (let i = 2; i < number; i += 1) if (number % i === 0) properFactors.push(i);
      // A prime has no proper factors, so there is nothing to offer as a
      // "yes" candidate — ask about a non-factor instead.
      const candidate =
        rng.bool() && properFactors.length > 0 ? rng.pick(properFactors) : rng.int(2, number - 1);
      const actuallyFactor = number % candidate === 0;
      const { choices, correctId } = options(
        [
          { label: 'Yes', correct: actuallyFactor },
          { label: 'No', correct: !actuallyFactor },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `Is ${candidate} a factor of ${number}?`,
        choices,
        correctId,
        summary: actuallyFactor
          ? `${number} ÷ ${candidate} = ${number / candidate} exactly, so yes.`
          : `${number} ÷ ${candidate} leaves a remainder of ${number % candidate}, so no.`,
        hint: 'A factor divides the number exactly, with nothing left over.',
        steps: [
          { label: 'Divide and look at the remainder.', latex: `${number} \\div ${candidate}` },
          { label: actuallyFactor ? 'No remainder — it is a factor.' : 'There is a remainder — it is not a factor.', latex: `\\text{remainder } ${number % candidate}` },
        ],
      });
    },
  }),

  template({
    id: 'gcf-lcm',
    conceptId: 'factors-multiples',
    description: 'Find the greatest common factor or least common multiple.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(4, difficulty === 3 ? 20 : 40);
      const b = rng.int(4, difficulty === 3 ? 20 : 40);
      const wantGcf = rng.bool();
      const answer = wantGcf ? gcd(a, b) : lcm(a, b);
      return numericExercise({
        prompt: wantGcf
          ? `What is the greatest common factor of ${a} and ${b}?`
          : `What is the least common multiple of ${a} and ${b}?`,
        value: answer,
        summary: wantGcf
          ? `The largest number dividing both ${a} and ${b} is ${answer}.`
          : `The smallest number that both ${a} and ${b} divide into is ${answer}.`,
        hint: wantGcf ? 'List the factors of each and find the biggest they share.' : 'Count up in multiples of the larger number until the smaller one divides it.',
        steps: [
          { label: wantGcf ? 'Greatest common factor.' : 'Least common multiple.', latex: `${wantGcf ? '\\gcd' : '\\operatorname{lcm}'}(${a}, ${b}) = ${answer}` },
        ],
        traps: [trap(wantGcf ? lcm(a, b) : gcd(a, b), 'divide-wrong-direction')],
      });
    },
  }),

  template({
    id: 'order-of-operations-eval',
    conceptId: 'order-of-operations',
    description: 'Evaluate an expression using the order of operations.',
    difficulties: [2, 3, 4, 5],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const c = rng.int(2, 9);
      if (difficulty <= 2) {
        return numericExercise({
          prompt: 'Evaluate.',
          latex: `${a} + ${b} \\times ${c}`,
          value: a + b * c,
          summary: `Multiplication first: ${b} × ${c} = ${b * c}, then add ${a}.`,
          hint: 'Multiplication happens before addition, whatever the order on the page.',
          steps: [
            { label: 'Multiply first.', latex: `${b} \\times ${c} = ${b * c}` },
            { label: 'Then add.', latex: `${a} + ${b * c} = ${a + b * c}` },
          ],
          traps: [trap((a + b) * c, 'multiply-then-add-wrong-order')],
        });
      }
      const d = rng.int(2, 6);
      const value = a + b * (c + d);
      return numericExercise({
        prompt: 'Evaluate.',
        latex: `${a} + ${b} \\times (${c} + ${d})`,
        value,
        summary: `Brackets first (${c} + ${d} = ${c + d}), then multiply by ${b}, then add ${a}.`,
        hint: 'Brackets, then multiply and divide, then add and subtract.',
        steps: [
          { label: 'Brackets.', latex: `${c} + ${d} = ${c + d}` },
          { label: 'Multiply.', latex: `${b} \\times ${c + d} = ${b * (c + d)}` },
          { label: 'Add.', latex: `${a} + ${b * (c + d)} = ${value}` },
        ],
        traps: [trap((a + b) * (c + d), 'multiply-then-add-wrong-order')],
      });
    },
  }),
];

function countOccurrences(digits: number[], digit: number): number {
  return digits.filter((d) => d === digit).length;
}
